import { Routes } from '@/api/Routes';
import { http } from '@/common/utils/HttpServices';
import { PeriodePlanning, Preventif } from '@/models/Preventif';
import dayjs, { Dayjs, OpUnitType } from 'dayjs';

export enum Periodicite {
    HEBDOMADAIRE = 'HEBDOMADAIRE',
    MENSUELLE = 'MENSUELLE',
}

export enum Vision {
    REELLE = 'REELLE',
    PREVISIONNELLE = 'PREVISIONNELLE',
}

export interface FilterPreventif {
    dateDebut: string;
    dateFin: string;
    periodicitePlanning: Periodicite;
    visionPlanning: Vision;
    siteId?: number;
    lastSiteId?: number;
    isMonoSite: boolean;
    isBim?: boolean;
}

export interface FilterIntervention {
    phaseIds?: string[];
    page: number;
    dateDebutPeriodeRechercheParDate?: string;
    dateFinPeriodeRechercheParDate?: string;
    sort?: string;
    phaseId?: number;
}

export enum PERIOD {
    BEFORE,
    NOW,
    AFTER,
}

class PreventifService {
    public async getListPreventif(params: FilterPreventif): Promise<any> {
        return http.get(Routes.PreventifRoutes.GetListPreventif(), { params });
    }

    public async getExportXlsInterventions(params: FilterPreventif): Promise<any> {
        return http.get(Routes.PreventifRoutes.GetInterventionsPreventivesXls(), {
            params,
            responseType: 'arraybuffer',
        });
    }

    public async getModalData(params: FilterIntervention): Promise<any> {
        return http.get(Routes.InterventionRoutes.GetInterventionsPreventives(), { params });
    }

    public async getInterventionsByPhases(params: FilterIntervention): Promise<any> {
        return http.get(Routes.InterventionRoutes.GetInterventionsNonPreventives(), { params });
    }
    public transformDisplayData(data: Preventif[], page: number, nbPeriodesAffichees: number): Preventif[] {
        return data.map((item: Preventif) => {
            return {
                id: item.id,
                libelle: item.libelle,
                interventionId: item.interventionId,
                periodesPlanning: item.periodesPlanning.slice(page, page + nbPeriodesAffichees),
                noeudsEnfants:
                    item.noeudsEnfants?.length > 0
                        ? this.transformDisplayData(item.noeudsEnfants, page, nbPeriodesAffichees)
                        : [],
            };
        });
    }

    public combinePreventiveData(
        dejaPresent: Preventif,
        newItems: Preventif[],
        emptyPlanning: any[],
        periode: PERIOD,
        header: any[],
        periodicite: Periodicite
    ): Preventif[] {
        const combinedData: Preventif[] = dejaPresent.noeudsEnfants?.map((y: Preventif) => ({
            id: y.id,
            libelle: y.libelle,
            interventionId: y.interventionId,
            periodesPlanning: this.concatenerPeriodesDesNoeuds(
                y,
                newItems?.filter(n => n.id === dejaPresent.id)[0]?.noeudsEnfants?.filter(n => n.id === y.id)[0],
                emptyPlanning,
                periode
            ),
            noeudsEnfants:
                !Array.isArray(y.noeudsEnfants) || !y.noeudsEnfants.length
                    ? []
                    : this.combinePreventiveData(
                          y,
                          newItems?.filter(n => n.id === dejaPresent.id)[0]?.noeudsEnfants,
                          emptyPlanning,
                          periode,
                          header,
                          periodicite
                      ),
        }));
        const newItem = newItems?.filter(n => n.id === dejaPresent.id)[0];
        if (newItem && dejaPresent.id === newItem.id) {
            this.addNewNoeudsEnfants(combinedData, newItem.noeudsEnfants, periode, header, periodicite);
        }
        return combinedData;
    }

    public concatenerPeriodesDesNoeuds(
        noeudExistant: Preventif,
        nouveauNoeud: Preventif,
        empty: PeriodePlanning[],
        periode: PERIOD
    ): PeriodePlanning[] {
        if (nouveauNoeud === undefined) {
            return this.concatenerPeriodes(noeudExistant.periodesPlanning, empty, periode);
        }
        if (noeudExistant.id === nouveauNoeud.id) {
            return this.concatenerPeriodes(noeudExistant.periodesPlanning, nouveauNoeud.periodesPlanning, periode);
        }
        return noeudExistant.periodesPlanning;
    }

    public addNewNoeudsEnfants(
        noeuxDejaPresents: Preventif[],
        nouveauxNoeuds: Preventif[],
        periode: PERIOD,
        header: any[],
        periodicite: Periodicite
    ) {
        if (nouveauxNoeuds !== undefined) {
            const existingIds = noeuxDejaPresents.map(n => n.id);
            nouveauxNoeuds.forEach(ne =>
                !existingIds.includes(ne.id)
                    ? this.addNewElement(noeuxDejaPresents, ne, periode, header, periodicite)
                    : null
            );
        }
    }

    public genererPeriodes(start: Dayjs, end: Dayjs, typePeriodicite: Periodicite) {
        const periodes: PeriodePlanning[] = [];
        let periodicite: OpUnitType = 'month';
        if (typePeriodicite === Periodicite.HEBDOMADAIRE) {
            periodicite = 'week';
        }
        const diff = end.diff(start, periodicite);
        for (let i = 0; i <= diff; i++) {
            periodes.push({
                dateDebut: start.add(i, periodicite).startOf(periodicite).format('YYYY-MM-DD'),
                dateFin: start.add(i, periodicite).endOf(periodicite).format('YYYY-MM-DD'),
                etat: '',
                isOberservationAlarmante: false,
            });
        }
        return periodes;
    }

    private concatenerPeriodes(
        periodesExistantes: PeriodePlanning[],
        nouvellesPeriodes: PeriodePlanning[],
        periode: PERIOD
    ) {
        if (periode === PERIOD.BEFORE) {
            return [...nouvellesPeriodes, ...periodesExistantes];
        } else if (periode === PERIOD.AFTER) {
            return [...periodesExistantes, ...nouvellesPeriodes];
        }
        return periodesExistantes;
    }

    private addNewElement(
        noeuxDejaPresents: Preventif[],
        noeud: Preventif,
        periode: PERIOD,
        header: any[],
        periodicite: Periodicite
    ) {
        this.genererPeriodesVidesManquantes(noeud, periode, header, periodicite);
        noeuxDejaPresents.push(noeud);
        noeuxDejaPresents.sort(function (first, second) {
            return first.id - second.id;
        });
    }

    private genererPeriodesVidesManquantes(noeud: Preventif, periode: PERIOD, header: any[], periodicite: Periodicite) {
        let periodiciteUnit: OpUnitType = 'month';
        if (periodicite === Periodicite.HEBDOMADAIRE) {
            periodiciteUnit = 'week';
        }
        let debut: Dayjs = dayjs();
        let fin: Dayjs = dayjs();
        let beforeOrAfter: PERIOD = PERIOD.NOW;
        if (periode === PERIOD.BEFORE) {
            fin = dayjs(header[header.length - 1].dateDebut);
            debut = dayjs(noeud.periodesPlanning[noeud.periodesPlanning.length - 1].dateDebut).add(1, periodiciteUnit);
            beforeOrAfter = PERIOD.AFTER;
        } else if (periode === PERIOD.AFTER) {
            debut = dayjs(header[0].dateDebut);
            fin = dayjs(noeud.periodesPlanning[0].dateDebut).subtract(1, periodiciteUnit);
            beforeOrAfter = PERIOD.BEFORE;
        }
        noeud.periodesPlanning = this.concatenerPeriodes(
            noeud.periodesPlanning,
            this.genererPeriodes(debut, fin, periodicite),
            beforeOrAfter
        );
        noeud.noeudsEnfants?.forEach(n => this.genererPeriodesVidesManquantes(n, periode, header, periodicite));
    }
}

export default new PreventifService();
